import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import {
  getTooltipPosition,
  type TooltipPosition,
  type TooltipSide,
} from "./tooltipPosition";

interface RepoActionPopoverProps {
  children: ReactNode;
  info: ReactNode;
  actions: { label: string; onClick: () => void }[];
  className?: string;
  side?: TooltipSide;
}

export const RepoActionPopover: FC<RepoActionPopoverProps> = ({
  children,
  info,
  actions,
  className,
  side = "right",
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    function updatePosition() {
      const triggerElement = triggerRef.current;
      const popoverElement = popoverRef.current;
      if (!triggerElement || !popoverElement) {
        return;
      }
      const triggerRect = triggerElement.getBoundingClientRect();
      const popoverRect = popoverElement.getBoundingClientRect();
      setPosition(
        getTooltipPosition(triggerRect, popoverRect, {
          align: "center",
          offset: 10,
          preferredSide: side,
          viewport: { height: window.innerHeight, width: window.innerWidth },
        }),
      );
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, side]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    function handleClickOutside(e: MouseEvent) {
      if (
        e.target instanceof Node &&
        (triggerRef.current?.contains(e.target) ||
          popoverRef.current?.contains(e.target))
      ) {
        return;
      }
      setIsOpen(false);
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls={isOpen ? popoverId : undefined}
        className={className ?? "inline-flex"}
        onClick={toggle}
      >
        {children}
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            id={popoverId}
            className="fixed z-[70] max-w-xs"
            style={
              position ? { left: position.left, top: position.top } : undefined
            }
          >
            <div className="border border-slate-300 bg-white/96 shadow-[6px_6px_0_rgba(15,23,42,0.12)] backdrop-blur-md dark:border-2 dark:border-slate-700 dark:bg-slate-950/92 dark:shadow-[8px_8px_0_rgba(2,6,23,0.35)]">
              <div className="px-3 py-2 font-mono text-[10px] font-bold uppercase leading-relaxed tracking-[0.14em] text-slate-700 dark:text-slate-100">
                {info}
              </div>
              {actions.length > 0 && (
                <div className="border-t border-slate-200 dark:border-slate-700">
                  {actions.map((action) => (
                    <button
                      key={action.label}
                      type="button"
                      className="flex w-full items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                      onClick={() => {
                        action.onClick();
                        setIsOpen(false);
                      }}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
